import { factoryService } from '../../services/factoryService';

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

export type FigureCropBox = { x: number; y: number; w: number; h: number };

export type FigureCropResult = {
  dataUrl: string;
  pageW: number;
  pageH: number;
  crop: { x: number; y: number; w: number; h: number };
  /** 实际用于切图的归一化框（含扩边） */
  boxUsed: FigureCropBox;
};

/**
 * 扩边：提取 figure_box 常偏紧。扩边后再切，减少投喂时丢边。
 * 贴边时整体平移（尽量保宽高），避免单侧 clamp 把有意义内容挤到一侧。
 */
export function expandNormalizedBox(
  box: FigureCropBox,
  opts?: { padFrac?: number; minPad?: number },
): FigureCropBox {
  const padFrac = opts?.padFrac ?? 0.06;
  const minPad = opts?.minPad ?? 0.008;
  const w = Math.max(0, clamp01(box.w));
  const h = Math.max(0, clamp01(box.h));
  const x0 = clamp01(box.x);
  const y0 = clamp01(box.y);
  const wideStrip = h > 0 && w / h >= 2.2;
  // 宽条图略加纵向余量即可，勿拉满页宽/过高，否则主体会「贴一侧」
  const padX = Math.max(minPad, w * (wideStrip ? padFrac * 0.5 : padFrac));
  const padY = wideStrip
    ? Math.max(minPad, Math.min(0.03, h * 0.18))
    : Math.max(minPad, h * padFrac);

  let x = x0 - padX;
  let y = y0 - padY;
  let x2 = x0 + w + padX;
  let y2 = y0 + h + padY;

  // 越界：先整体平移保住尺寸，再必要时收缩
  if (x < 0) {
    x2 -= x;
    x = 0;
  }
  if (y < 0) {
    y2 -= y;
    y = 0;
  }
  if (x2 > 1) {
    x -= x2 - 1;
    x2 = 1;
  }
  if (y2 > 1) {
    y -= y2 - 1;
    y2 = 1;
  }
  if (x < 0) x = 0;
  if (y < 0) y = 0;
  x2 = Math.min(1, Math.max(x + 0.001, x2));
  y2 = Math.min(1, Math.max(y + 0.001, y2));

  return {
    x,
    y,
    w: Math.max(0.001, x2 - x),
    h: Math.max(0.001, y2 - y),
  };
}

/** Crop a structural-unit page JPEG by normalized figure_box. */
export async function cropPageImageByBox(
  pageImageUrl: string,
  box: FigureCropBox,
  opts?: { expand?: boolean },
): Promise<FigureCropResult> {
  const boxUsed = opts?.expand === false ? {
    x: clamp01(box.x),
    y: clamp01(box.y),
    w: Math.max(0.001, clamp01(box.w)),
    h: Math.max(0.001, clamp01(box.h)),
  } : expandNormalizedBox(box);

  const token = await factoryService.getAccessToken();
  const res = await fetch(pageImageUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json() as { message?: string; error?: string };
      detail = String(body.message || body.error || detail);
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  if (!blob.size) throw new Error('页图为空');
  const bitmap = await createImageBitmap(blob);
  const pageW = bitmap.width;
  const pageH = bitmap.height;
  const x = Math.floor(boxUsed.x * pageW);
  const y = Math.floor(boxUsed.y * pageH);
  const w = Math.max(1, Math.floor(boxUsed.w * pageW));
  const h = Math.max(1, Math.floor(boxUsed.h * pageH));
  const sx = Math.min(Math.max(0, x), pageW - 1);
  const sy = Math.min(Math.max(0, y), pageH - 1);
  const sw = Math.min(w, pageW - sx);
  const sh = Math.min(h, pageH - sy);
  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('canvas_unavailable');
  }
  ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, sw, sh);
  bitmap.close();
  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.92),
    pageW,
    pageH,
    crop: { x: sx, y: sy, w: sw, h: sh },
    boxUsed,
  };
}
